import {
  OrganizationDTO,
  OrganizationQRDTO,
  ParticipantDTO,
} from "@/generated/api/models";
import { useCallback, useMemo, useRef } from "react";
import { getName } from "./name";
import { useToast } from "./useToast";
import { Svg } from "react-native-svg";
import { Buffer } from "buffer";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import * as Clipboard from "expo-clipboard";
import { Platform } from "react-native";

export function useQRCode(
  props:
    | {
        participant: Pick<
          ParticipantDTO,
          "id" | "role" | "user" | "publicTags" | "privateTags" | "number"
        >;
      }
    | {
        organization: Pick<OrganizationDTO, "id" | "name" | "number">;
        tipJar: boolean;
      }
    | {
        qr: Pick<OrganizationQRDTO, "id" | "number" | "internalCode">;
      }
    | {
        arbitraryUrl: string;
      }
) {
  return useMemo(() => {
    if ("participant" in props) {
      return {
        filename: getName(props.participant.user),
        link:
          `${process.env.EXPO_PUBLIC_QR_BASE_URL}/p/` +
          props.participant.number,
      };
    } else if ("qr" in props) {
      return {
        filename: props.qr.internalCode,
        link: `${process.env.EXPO_PUBLIC_QR_BASE_URL}/q/` + props.qr.number,
      };
    } else if ("organization" in props) {
      return {
        filename: props.organization.name,
        link:
          `${process.env.EXPO_PUBLIC_QR_BASE_URL}/o/` +
          props.organization.number +
          (props.tipJar ? "/tip" : ""),
      };
    } else {
      return {
        filename: "link",
        link: props.arbitraryUrl,
      };
    }
  }, [props]);
}

export function useQRCodeDownloader(props: Parameters<typeof useQRCode>[0]) {
  const { showToast } = useToast();
  const { filename, link } = useQRCode(props);
  const qrCodeRef = useRef<Svg>(null);

  const onCopy = useCallback(() => {
    showToast("success", "Link copied!");
    Clipboard.setStringAsync(link);
  }, [link]);

  const onShare = useCallback(() => {
    qrCodeRef.current?.toDataURL(async (base64) => {
      if (Platform.OS === "web") {
        const buffer = Buffer.from(base64, "base64");
        const blob = new Blob([buffer], { type: "image/png" });
        const link = document.createElement("a");
        link.download = filename;
        link.href = window.URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        if (!(await Sharing.isAvailableAsync())) {
          console.error("Sharing is not available");
          return;
        }

        if (FileSystem.cacheDirectory) {
          const temporaryFileUri = `${FileSystem.cacheDirectory}/${filename}.png`;
          await FileSystem.writeAsStringAsync(temporaryFileUri, base64, {
            encoding: "base64",
          });
          await Sharing.shareAsync(temporaryFileUri);
        }
      }
    });
  }, [link, filename]);

  return {
    link,
    filename,
    onCopy,
    onShare,
    qrCodeRef,
  };
}
